import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, FileText, CreditCard, Clock, XCircle, Ban, LogOut, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth, useAccessControl } from '@/context/AuthContext';

const AcessoBloqueado = () => {
  const navigate = useNavigate();
  const { usuario, empresa, logout } = useAuth();
  const { blockReason, redirectTo } = useAccessControl();
  
  const getBlockContent = () => {
    switch (blockReason) {
      case 'complete_registration':
        return {
          icon: <FileText className="w-16 h-16 text-orange-500" />,
          title: 'Complete seu Cadastro',
          description: 'Para utilizar a plataforma, você precisa enviar os documentos obrigatórios da sua empresa.',
          actionText: 'Enviar Documentos',
          actionPath: `/dashboard/${usuario?.perfil}/documentos`,
        };
      case 'pending_approval':
        return {
          icon: <Clock className="w-16 h-16 text-blue-500" />,
          title: 'Cadastro em Análise',
          description: 'Seus documentos estão sendo analisados pela nossa equipe. Você receberá uma notificação assim que o processo for concluído.',
          actionText: 'Ver Status dos Documentos',
          actionPath: `/dashboard/${usuario?.perfil}/documentos`,
        };
      case 'rejected':
        return {
          icon: <XCircle className="w-16 h-16 text-red-500" />,
          title: 'Cadastro Rejeitado',
          description: 'Alguns documentos foram rejeitados. Por favor, verifique os motivos e reenvie os documentos corrigidos.',
          actionText: 'Corrigir Documentos',
          actionPath: `/dashboard/${usuario?.perfil}/documentos`,
        };
      case 'blocked':
        return {
          icon: <Ban className="w-16 h-16 text-red-600" />,
          title: 'Acesso Bloqueado',
          description: 'Sua empresa foi bloqueada. Entre em contato com o suporte para mais informações.',
          actionText: null,
          actionPath: null,
        };
      case 'no_subscription':
        return {
          icon: <CreditCard className="w-16 h-16 text-yellow-500" />,
          title: 'Assinatura Necessária',
          description: 'Sua assinatura não está ativa. Realize o pagamento para ter acesso completo à plataforma.',
          actionText: 'Ir para Financeiro',
          actionPath: `/dashboard/${usuario?.perfil}/financeiro`,
        };
      default:
        return {
          icon: <AlertTriangle className="w-16 h-16 text-gray-500" />,
          title: 'Acesso Restrito',
          description: 'Você não tem permissão para acessar esta área.',
          actionText: null,
          actionPath: null,
        };
    }
  };
  
  const content = getBlockContent();
  
  const handleAction = () => {
    if (content.actionPath) {
      navigate(content.actionPath);
    }
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="flex justify-center mb-6">
          {content.icon}
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          {content.title}
        </h1>
        
        <p className="text-gray-600 mb-6">
          {content.description}
        </p>
        
        {empresa && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-gray-500">Empresa</p>
            <p className="font-medium text-gray-900">{empresa.razao_social}</p>
            <p className="text-sm text-gray-500 mt-2">Status</p>
            <p className="font-medium text-gray-900 capitalize">
              {empresa.status_validacao?.replace(/_/g, ' ') || 'Não definido'}
            </p>
          </div>
        )}
        
        <div className="space-y-3">
          {content.actionText && (
            <Button 
              onClick={handleAction}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="access-blocked-action-btn"
            >
              {content.actionText}
            </Button>
          )}
          
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="w-full"
            data-testid="access-blocked-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair da Conta
          </Button>
          
          <Link to="/" className="block">
            <Button 
              variant="ghost" 
              className="w-full text-gray-500 hover:text-orange-600"
              data-testid="back-to-home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Voltar ao Início
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AcessoBloqueado;
